import { BarChart3, FolderOpen, Map as MapIcon, TableProperties, Upload } from "lucide-react";
import { useCallback, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CampaignCharts } from "./CampaignCharts";
import { CampaignMap } from "./CampaignMap";
import { KpiCards } from "./KpiCards";
import { LocationsTable } from "./LocationsTable";
import { MapLegend, type ScoreFilter } from "./MapLegend";
import { useChartData } from "./useChartData";

/** KPI strip pinned above the locations table (mounts only on the table tab). */
function TableKpiStrip({
  campaignId,
  profileId,
  refreshKey,
}: {
  campaignId: string;
  profileId: string | null;
  refreshKey: number;
}) {
  const { data, loading } = useChartData(campaignId, profileId, refreshKey);

  if (loading && !data) {
    return (
      <div className="grid shrink-0 grid-cols-2 gap-4 border-b border-border bg-background px-4 py-3 lg:grid-cols-3 xl:grid-cols-6">
        {["a", "b", "c", "d", "e", "f"].map((k) => (
          <div key={k} className="h-20 animate-pulse rounded-lg border border-border bg-card" />
        ))}
      </div>
    );
  }
  if (!data || data.locations.length === 0) return null;

  return (
    <div className="shrink-0 border-b border-border bg-background px-4 py-3">
      <KpiCards data={data} />
    </div>
  );
}

interface ResultsPanelProps {
  campaignId: string | null;
  profileId: string | null;
  campaignName: string;
  locationCount: number;
  view: "table" | "map" | "charts";
  onViewChange: (view: string) => void;
  refreshKey: number;
}

export function ResultsPanel({
  campaignId,
  profileId,
  campaignName: _campaignName,
  locationCount,
  view,
  onViewChange,
  refreshKey,
}: ResultsPanelProps) {
  const [scoreFilters, setScoreFilters] = useState<Set<ScoreFilter>>(() => new Set());

  const handleToggleFilter = useCallback((filter: ScoreFilter) => {
    setScoreFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  }, []);

  if (!campaignId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center p-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
          <FolderOpen className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium">Select a campaign to view results</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose a campaign from the left panel to see its locations and scores.
          </p>
        </div>
      </div>
    );
  }

  if (locationCount === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center p-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
          <Upload className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium">Upload addresses to get started</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Use the upload area in the campaign panel to add locations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Tabs
        value={view}
        onValueChange={onViewChange}
        className="relative flex flex-1 flex-col overflow-hidden gap-0"
      >
        <div
          className={cn(
            "flex items-center justify-center px-4 py-2.5",
            view === "map"
              ? "absolute top-0 left-0 right-0 z-20 bg-transparent"
              : "shrink-0 bg-background border-b border-border",
          )}
        >
          <TabsList
            className={cn("h-9", view === "map" && "bg-background/80 backdrop-blur-sm shadow-lg")}
          >
            <TabsTrigger value="table" className="gap-1.5 text-xs px-3">
              <TableProperties className="h-3.5 w-3.5" />
              Table
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-1.5 text-xs px-3">
              <MapIcon className="h-3.5 w-3.5" />
              Map
            </TabsTrigger>
            <TabsTrigger value="charts" className="gap-1.5 text-xs px-3">
              <BarChart3 className="h-3.5 w-3.5" />
              Charts
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="table" className="relative flex-1 overflow-hidden p-0">
          <div className="flex h-full flex-col">
            <TableKpiStrip campaignId={campaignId} profileId={profileId} refreshKey={refreshKey} />
            <div className="min-h-0 flex-1 overflow-hidden">
              <LocationsTable
                campaignId={campaignId}
                profileId={profileId}
                refreshKey={refreshKey}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="map" className="relative flex-1 overflow-hidden">
          <MapLegend activeFilters={scoreFilters} onToggle={handleToggleFilter} />
          <CampaignMap
            campaignId={campaignId}
            profileId={profileId}
            refreshKey={refreshKey}
            scoreFilters={scoreFilters}
          />
        </TabsContent>

        <TabsContent value="charts" className="flex-1 overflow-y-auto">
          <CampaignCharts campaignId={campaignId} profileId={profileId} refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
